import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ClubBadge } from '@/components/ClubBadge';
import { Search, ChevronRight, Sparkles, Trophy, Target, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';

interface Player {
  id: string;
  full_name: string;
  position: string;
  club: string;
  nationality: string;
  date_of_birth: string;
}

interface OnboardingTutorialProps {
  clubs: string[];
  onComplete: () => void;
}

export function OnboardingTutorial({ clubs, onComplete }: OnboardingTutorialProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<'welcome' | 'name' | 'club-select' | 'player-suggest'>('welcome');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClub, setSelectedClub] = useState<string | null>(null);
  const [suggestedPlayer, setSuggestedPlayer] = useState<Player | null>(null);
  const [loading, setLoading] = useState(false);
  const [fullName, setFullName] = useState('');
  const [savingName, setSavingName] = useState(false);

  // Prefill the name field with the existing profile name (unless it's the default placeholder).
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('user_id', user.id)
        .maybeSingle();
      if (cancelled) return;
      if (data?.full_name && data.full_name !== 'New Scout') {
        setFullName(data.full_name);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const persistOnboarding = async (
    patch: { full_name?: string; favorite_club?: string },
  ) => {
    if (!user) return;
    const { error } = await supabase
      .from('profiles')
      .update({ ...patch, onboarding_completed: true })
      .eq('user_id', user.id);
    if (error) {
      console.error('Failed to save onboarding profile:', error);
      toast.error('Could not save your profile. Please try again.');
      return false;
    }
    return true;
  };

  const filteredClubs = clubs.filter(club =>
    club.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleNameSubmit = async () => {
    const trimmed = fullName.trim();
    if (trimmed.length < 2) {
      toast.error('Please enter your name (at least 2 characters).');
      return;
    }
    if (trimmed.length > 100) {
      toast.error('Name must be 100 characters or fewer.');
      return;
    }
    setSavingName(true);
    const ok = await persistOnboarding({ full_name: trimmed });
    setSavingName(false);
    if (ok) setStep('club-select');
  };

  const handleClubSelect = async (club: string) => {
    setSelectedClub(club);
    setLoading(true);

    // Save favorite club + mark onboarding complete as soon as the user picks one.
    await persistOnboarding({ favorite_club: club });

    try {
      const { data, error } = await supabase
        .from('players')
        .select('*')
        .eq('club', club)
        .limit(10);

      if (!error && data && data.length > 0) {
        // Pick a random player from the club
        const randomPlayer = data[Math.floor(Math.random() * data.length)];
        setSuggestedPlayer(randomPlayer);
      }
    } catch (err) {
      console.error('Error fetching player:', err);
    } finally {
      setLoading(false);
      setStep('player-suggest');
    }
  };

  const handleStartScouting = () => {
    if (suggestedPlayer) {
      // Navigate to scouting session — guided mode will be default
      navigate(`/scout/${suggestedPlayer.id}?mode=guided`);
    }
    onComplete();
  };

  const handleSkip = async () => {
    // Skipping still marks onboarding complete so we don't prompt again.
    await persistOnboarding({});
    onComplete();
  };

  const calculateAge = (dob: string | null | undefined) => {
    if (!dob) return null;
    const birth = new Date(dob);
    if (isNaN(birth.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  return (
    <div className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-xl flex items-center justify-center p-4 overflow-y-auto">
      <div aria-hidden="true" className="pointer-events-none absolute top-5 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
        {['welcome', 'name', 'club-select', 'player-suggest'].map((s) => (
          <span
            key={s}
            className={`h-1.5 rounded-full transition-all duration-[220ms] ease-out ${step === s ? 'w-6 bg-primary' : 'w-1.5 bg-border'}`}
          />
        ))}
      </div>
      <AnimatePresence mode="wait">
        {/* Step 1: Welcome */}
        {step === 'welcome' && (
          <motion.div
            key="welcome"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="max-w-lg w-full text-center space-y-8"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-primary/10 mb-2">
              <Sparkles className="h-10 w-10 text-primary" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-display font-bold tracking-[-0.03em] mb-3">
                Welcome to HalftimeScout
              </h1>
              <p className="text-muted-foreground text-lg">
                Let's get you started with a quick scouting session. First, pick your favorite club!
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <Button size="lg" onClick={() => setStep('name')} className="h-12 text-base gap-2">
                Let's go <ChevronRight className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={handleSkip} className="text-muted-foreground">
                Skip tutorial
              </Button>
            </div>
          </motion.div>
        )}

        {/* Step 1b: Name */}
        {step === 'name' && (
          <motion.div
            key="name"
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40 }}
            transition={{ duration: 0.3 }}
            className="max-w-lg w-full space-y-6"
          >
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-primary/10 mb-4">
                <UserIcon className="h-7 w-7 text-primary" />
              </div>
              <h2 className="text-3xl font-display font-bold tracking-[-0.025em] mb-2">What should we call you?</h2>
              <p className="text-muted-foreground text-sm">
                Your scout name appears on your profile and reports.
              </p>
            </div>

            <Input
              placeholder="Your full name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              maxLength={100}
              className="h-11"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleNameSubmit();
              }}
            />

            <div className="flex flex-col gap-3">
              <Button
                size="lg"
                onClick={handleNameSubmit}
                disabled={savingName || fullName.trim().length < 2}
                className="h-12 text-base gap-2"
              >
                {savingName ? 'Saving…' : <>Continue <ChevronRight className="h-4 w-4" /></>}
              </Button>
              <Button variant="ghost" size="sm" onClick={handleSkip} className="text-muted-foreground">
                Skip tutorial
              </Button>
            </div>
          </motion.div>
        )}

        {/* Step 2: Club Selection */}
        {step === 'club-select' && (
          <motion.div
            key="club-select"
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40 }}
            transition={{ duration: 0.3 }}
            className="max-w-lg w-full space-y-6"
          >
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-primary/10 mb-4">
                <Trophy className="h-7 w-7 text-primary" />
              </div>
              <h2 className="text-3xl font-display font-bold tracking-[-0.025em] mb-2">Choose your club</h2>
              <p className="text-muted-foreground text-sm">
                Select your favorite club and we'll suggest a player for you to scout.
              </p>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search clubs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-11"
                autoFocus
              />
            </div>

            <div className="max-h-[340px] overflow-y-auto space-y-1.5 pr-1 scrollbar-thin">
              {filteredClubs.map((club) => (
                <motion.button
                  key={club}
                  onClick={() => handleClubSelect(club)}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-border/70 bg-card hover:border-primary/40 hover:ring-1 hover:ring-primary/30 hover:shadow-card focus-visible:ring-2 focus-visible:ring-ring transition-all duration-[140ms] ease-out text-left"
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  <ClubBadge club={club} />
                  <span className="font-medium text-sm">{club}</span>
                </motion.button>
              ))}
              {filteredClubs.length === 0 && (
                <p className="text-center text-muted-foreground text-sm py-8">No clubs found</p>
              )}
            </div>

            <div className="text-center">
              <Button variant="ghost" size="sm" onClick={handleSkip} className="text-muted-foreground">
                Skip tutorial
              </Button>
            </div>
          </motion.div>
        )}

        {/* Step 3: Player Suggestion */}
        {step === 'player-suggest' && (
          <motion.div
            key="player-suggest"
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40 }}
            transition={{ duration: 0.3 }}
            className="max-w-lg w-full space-y-6"
          >
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-primary/10 mb-4">
                <Target className="h-7 w-7 text-primary" />
              </div>
              <h2 className="text-3xl font-display font-bold tracking-[-0.025em] mb-2">Your first scouting target</h2>
              <p className="text-muted-foreground text-sm">
                We've picked a player from <strong>{selectedClub}</strong> for you to try out Guided Scouting.
              </p>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="w-full space-y-3" aria-hidden="true"><div className="skeleton h-12 rounded-xl" /><div className="skeleton h-24 rounded-2xl" /></div>
              </div>
            ) : suggestedPlayer ? (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="rounded-2xl border border-primary/30 bg-card ring-1 ring-primary/20 shadow-glow p-6 space-y-4"
              >
                <div className="flex items-center gap-3">
                  <ClubBadge club={suggestedPlayer.club} />
                  <div>
                    <h3 className="font-display font-bold text-xl">{suggestedPlayer.full_name}</h3>
                    <p className="text-sm text-muted-foreground">{suggestedPlayer.club}</p>
                  </div>
                </div>
                <div className="flex gap-6 text-sm">
                  <div>
                    <span className="text-muted-foreground">Position</span>
                    <p className="font-semibold capitalize">{suggestedPlayer.position}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Age</span>
                    <p className="font-semibold">{calculateAge(suggestedPlayer.date_of_birth) ?? '—'}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Nationality</span>
                    <p className="font-semibold">{suggestedPlayer.nationality}</p>
                  </div>
                </div>
              </motion.div>
            ) : (
              <p className="text-center text-muted-foreground py-8">
                No players found for this club. Try another club.
              </p>
            )}

            <div className="flex flex-col gap-3">
              {suggestedPlayer && (
                <Button size="lg" onClick={handleStartScouting} className="h-12 text-base gap-2">
                  Start Guided Scouting <ChevronRight className="h-4 w-4" />
                </Button>
              )}
              <div className="flex justify-center gap-4">
                <Button variant="ghost" size="sm" onClick={() => { setStep('club-select'); setSearchQuery(''); }} className="text-muted-foreground">
                  Choose another club
                </Button>
                <Button variant="ghost" size="sm" onClick={handleSkip} className="text-muted-foreground">
                  Skip to dashboard
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
